import NavBar from "../../components/common/NavBar";
import React, { useMemo, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { DELETE_VENUE, GET_VENUES_QUERY } from "../../components/venue/Venue";
import PaginatedFlatTable from "../../components/pagination/PaginatedFlatTable";
import VenueCreateModal from "../../components/venue/VenueCreateModal";
import { toast } from "react-toastify";
import VenueEditModal from "../../components/venue/VenueEditModal";
import { confirmDelete } from "../../components/common/confirmDelete";
import { GlobalStyles } from "../../constants/styles";
import useActiveProfile from "../../components/common/hooks/useActiveProfile";
import useTranslation from "../../i18n/useTranslation";
import { getLocalizedGraphQLError } from "../../util/graphqlError";

export function VenuesPage() {
    const scopeId = "kbola-venues-page";
    const { t } = useTranslation();

    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const pageSize = 25;

    const { activeProfile } = useActiveProfile();

    const [venues, setVenues] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [selectedVenue, setSelectedVenue] = useState(null);
    const [editModalOpen, setEditModalOpen] = useState(false);

    const ui = useMemo(() => {
        return {
            section: { padding: "0 10px 14px" },

            noticeWrap: {
                maxWidth: 900,
                width: "100%",
                borderRadius: 18,
                overflow: "hidden",
                border: "1px solid rgba(0,0,0,0.08)",
                background: "#fff",
                boxShadow: "0 18px 44px rgba(0,0,0,0.10)",
            },
            noticeHeader: {
                padding: "12px 14px",
                background: "linear-gradient(135deg, rgba(var(--kbs-primary-rgb, 0,173,255), 0.16), rgba(0,0,0,0))",
                borderBottom: "1px solid rgba(0,0,0,0.06)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
            },
            noticeTitleWrap: { display: "flex", alignItems: "center", gap: 10, minWidth: 0 },
            badge: {
                fontSize: 12,
                fontWeight: 900,
                letterSpacing: "0.03em",
                padding: "6px 10px",
                borderRadius: 999,
                background: "rgba(var(--kbs-primary-rgb, 0,173,255), 0.95)",
                border: "1px solid rgba(0,0,0,0.10)",
                color: GlobalStyles.colors.woodsmoke,
                whiteSpace: "nowrap",
            },
            noticeTitle: {
                margin: 0,
                fontWeight: 1000,
                color: "#14151a",
                fontSize: 14,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
            },
            noticeSub: {
                margin: 0,
                fontSize: 12.5,
                fontWeight: 700,
                color: "rgba(20,21,26,0.55)",
            },
            noticeBody: { padding: 14 },
            noticeP: { margin: 0, fontSize: 14, fontWeight: 700, color: "rgba(20,21,26,0.75)" },
            noticeLink: { fontWeight: 1000, textDecoration: "underline" },

            chip: {
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
                padding: "6px 10px",
                borderRadius: 999,
                border: "1px solid rgba(0,0,0,0.10)",
                background: "rgba(0,0,0,0.02)",
                color: "rgba(20,21,26,0.78)",
                fontWeight: 900,
                fontSize: 12,
                lineHeight: 1,
                whiteSpace: "nowrap",
                marginBottom: 6,
            },

            actionBtn: {
                width: 38,
                height: 38,
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.10)",
                background: "#fff",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
            },
            actionBtnEdit: {
                border: "1px solid rgba(0,0,0,0.10)",
                background: "rgba(0,0,0,0.02)",
                color: "#14151a",
            },
            actionBtnDanger: {
                border: "1px solid rgba(220,53,69,0.35)",
                background: "rgba(220,53,69,0.06)",
                color: "#dc3545",
            },
        };
    }, []);

    const { loading, error, refetch } = useQuery(GET_VENUES_QUERY, {
        variables: { page, pageSize },
        skip: !activeProfile,
        fetchPolicy: "cache-and-network",
        onCompleted: (data) => {
            setVenues(data?.venues?.objects || []);
            setTotalPages(data?.venues?.pages || 1);
        },
    });

    const [deleteVenue] = useMutation(DELETE_VENUE);

    const handleDelete = (venue) => {
        confirmDelete({
            title: t("venuesPage.deleteConfirmTitle"),
            message: t("venuesPage.deleteConfirmMessage", { name: venue.name }),
            confirmLabel: t("venuesPage.deleteConfirmButton"),
            cancelLabel: t("venuesPage.cancel"),
            danger: true,
            onConfirm: async () => {
                try {
                    const { data } = await deleteVenue({ variables: { id: venue.id } });
                    if (data?.deleteVenue?.success) {
                        setVenues((prev) => prev.filter((w) => w.id !== venue.id));
                        toast.success(t("venuesPage.deleteSuccess"), {
                            autoClose: 3000,
                            toastId: "venue_deleted",
                            position: "top-center",
                            closeOnClick: true,
                        });
                    } else {
                        toast.error(t("venuesPage.deleteFailed"), {
                            autoClose: 3000,
                            toastId: "venue_delete_failed",
                            position: "top-center",
                            closeOnClick: true,
                        });
                    }
                } catch (err) {
                    toast.error(t("venuesPage.deleteFailed"), {
                        autoClose: 3000,
                        toastId: "venue_delete_error",
                        position: "top-center",
                        closeOnClick: true,
                    });
                }
            },
        });
    };

    const navBarElem = {
        pageTitle: t("venuesPage.pageTitle"),
        links: [
            { pageTitle: t("venuesPage.settings"), navLink: null, active: false },
            { pageTitle: t("venuesPage.pageTitle"), navLink: null, active: true },
        ],
    };

    return (
        <>
            <div className="content-wrapper" id={scopeId}>
                <NavBar navBarElem={navBarElem} />

                <section className="content" style={ui.section}>
                    {error ? (
                        <div className="alert alert-danger col-6 mx-auto text-center">
                            {getLocalizedGraphQLError(error, t, "venuesPage.loadFailed")}
                        </div>
                    ) : null}

                    <PaginatedFlatTable
                        title={t("venuesPage.tableTitle")}
                        page={page}
                        pages={totalPages}
                        onPageChange={(p) => setPage(p)}
                        onAdd={() => setShowModal(true)}
                        items={venues}
                        loading={loading}
                        headers={[
                            t("venuesPage.headers.name"),
                            t("venuesPage.headers.description"),
                            t("venuesPage.headers.fields"),
                            t("venuesPage.headers.createdBy"),
                            t("venuesPage.headers.actions"),
                        ]}
                        renderRow={(venue, index) => (
                            <tr key={venue.id || index}>
                                <td style={{ fontWeight: 900, color: "#14151a" }}>
                                    {venue.name || t("venuesPage.noValue")}
                                </td>

                                <td style={{ color: "rgba(20,21,26,0.78)", fontWeight: 700 }}>
                                    {venue.description || t("venuesPage.noValue")}
                                </td>

                                <td>
                                    {venue.fields?.length ? (
                                        <div style={{ display: "flex", flexDirection: "column" }}>
                                            {venue.fields.map((f, i) => (
                                                <span key={i} style={ui.chip} title={t("venuesPage.fieldTitle")}>
                                                    <i className="fas fa-map-marker-alt" />
                                                    {f.name}
                                                </span>
                                            ))}
                                        </div>
                                    ) : (
                                        t("venuesPage.noValue")
                                    )}
                                </td>

                                <td style={{ color: "rgba(20,21,26,0.70)", fontWeight: 800 }}>
                                    {venue.createdBy?.email || t("venuesPage.noValue")}
                                </td>

                                <td className="text-right">
                                    <div style={{ display: "inline-flex", gap: 8 }}>
                                        <button
                                            type="button"
                                            style={ui.actionBtn}
                                            className="kbola-action kbola-action-edit"
                                            onClick={() => {
                                                setSelectedVenue(venue);
                                                setEditModalOpen(true);
                                            }}
                                            title={t("venuesPage.editVenue")}
                                        >
                                            <i className="fas fa-edit" />
                                        </button>

                                        <button
                                            type="button"
                                            style={{ ...ui.actionBtn, ...ui.actionBtnDanger }}
                                            className="kbola-action kbola-action-danger"
                                            onClick={() => handleDelete(venue)}
                                            title={t("venuesPage.deleteVenue")}
                                        >
                                            <i className="fas fa-trash" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        )}
                    />
                </section>

                <style>{`
          #${scopeId}{
            font-family: 'Poppins', sans-serif;
          }

          #${scopeId} .kbola-action{
            width: 38px;
            height: 38px;
            border-radius: 12px;
            border: 1px solid rgba(0,0,0,0.10);
            background: #fff;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            padding: 0;
          }

          #${scopeId} .kbola-action-edit{
            border: 1px solid rgba(0,0,0,0.10);
            background: rgba(0,0,0,0.02);
            color: #14151a;
          }

          #${scopeId} .kbola-action-danger{
            border: 1px solid rgba(220,53,69,0.35);
            background: rgba(220,53,69,0.06);
            color: #dc3545;
          }

          #${scopeId} button:focus,
          #${scopeId} .btn:focus,
          #${scopeId} .form-control:focus{
            outline: none;
            box-shadow: 0 0 0 0.2rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.18);
            border-color: rgba(var(--kbs-primary-rgb, 0,173,255), 0.65);
          }

          #${scopeId} i.fas,
          #${scopeId} i.far,
          #${scopeId} i.fal,
          #${scopeId} i.fa,
          #${scopeId} i[class^="fa-"],
          #${scopeId} i[class*=" fa-"] {
            font-family: "Font Awesome 5 Free" !important;
            font-weight: 900;
          }

          #${scopeId} i.fab {
            font-family: "Font Awesome 5 Brands" !important;
            font-weight: 400;
          }
        `}</style>
            </div>

            <VenueCreateModal
                onCreated={(newVenue) => {
                    setVenues((prev) => [newVenue, ...prev]);
                    setPage(1);
                }}
                onClose={() => setShowModal(false)}
                show={showModal}
            />

            <VenueEditModal
                show={editModalOpen}
                onClose={() => setEditModalOpen(false)}
                venue={selectedVenue}
                refetch={refetch}
            />
        </>
    );
}
